import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Card} from 'components';
import {TRANSLATION_KEYS} from 'locale';

import type {TimePerStageResult, VersionLabel} from './types';
import {preSessionFlowAnalyticsUtils} from './utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACTIVITY.PRE_SESSION_FLOW.TIME_PER_STAGE;

type Props = {
    data?: TimePerStageResult[];
    versions: VersionLabel[];
    isLoading: boolean;
};

export const PreSessionFlowTimePerStage = ({data, versions, isLoading}: Props) => {
    const {t} = useTranslation();
    const labelMap = useMemo(() => preSessionFlowAnalyticsUtils.buildVersionLabelMaps(versions).stepNames, [versions]);
    const maxAvg = useMemo(() => {
        const ms = (data || []).map((s) => s.avgDurationMs);
        return ms.length === 0 ? 0 : Math.max(...ms);
    }, [data]);

    if (isLoading) {
        return <Card><div className='tw-text-sm tw-text-default-dimmed'>{t(T_KEYS.LOADING)}</div></Card>;
    }
    if (!data || data.length === 0) {
        return <Card><div className='tw-text-sm tw-text-default-dimmed'>{t(T_KEYS.NO_DATA)}</div></Card>;
    }

    return (
        <Card>
            <h3 className='tw-ts-h5-bold tw-mb-3'>{t(T_KEYS.TITLE)}</h3>
            <div className='tw-flex tw-flex-col tw-gap-3'>
                {data.map((row, idx) => {
                    const name = labelMap.get(row.stepId) || `Step ${row.stepId.slice(0, 6)}`;
                    const hasData = row.runs > 0;
                    const widthPct = hasData && maxAvg > 0 ? Math.round((row.avgDurationMs / maxAvg) * 100) : 0;
                    return (
                        <div key={row.stepId} className='tw-flex tw-items-center tw-gap-3'>
                            <div className='tw-w-6 tw-text-xs tw-text-default-dimmed tw-text-right tw-flex-shrink-0'>
                                {String(idx + 1).padStart(2, '0')}
                            </div>
                            <div className='tw-flex-1 tw-min-w-0'>
                                <div className='tw-text-sm tw-truncate tw-mb-1'>{name}</div>
                                <div className='tw-h-2 tw-bg-layout tw-rounded tw-overflow-hidden'>
                                    <div
                                        className='tw-h-full tw-bg-primary tw-transition-all'
                                        style={{width: `${widthPct}%`}}
                                    />
                                </div>
                            </div>
                            <div className='tw-w-20 tw-text-right tw-text-sm tw-tabular-nums tw-flex-shrink-0'>
                                {hasData
                                    ? preSessionFlowAnalyticsUtils.formatStageDuration(row.avgDurationMs, '<1s')
                                    : <span className='tw-text-default-dimmed'>{'—'}</span>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};
